import React from 'react';
import clsx from 'clsx';
import ClientPortal from '~/components/global/ClientPortal';
import {MARSELLO_PORTAL_URL} from '~/lib/const';
import {ICON_BUTTON} from './Sidebar';

const PORTAL_ORIGIN = new URL(MARSELLO_PORTAL_URL).origin;

/**
 * 會員專區 — Marsello's hosted customer portal in a right-side overlay,
 * without leaving the page.
 *
 * The portal document ships a TRANSPARENT background (html/body
 * background-color:#fffff000) and renders itself as a floating card on
 * desktop — it is designed to be embedded in a transparent iframe. Our
 * first drawer attempt wrapped it in an opaque white panel, which is
 * exactly where the "white gutters" came from; this version keeps the
 * overlay transparent so only the portal's own card is visible.
 *
 * Sign-in: inside an iframe the portal deliberately does NOT render its
 * sign-in pages — it posts 'signin' / 'signup' to the parent window and
 * expects the host page to take over (its postMessage page-control API
 * only accepts origins in account.allowedHosts, which currently lists
 * only Marsello's own dashboard). We handle the message by opening the
 * portal in a new top-level tab, where login always works (first-party
 * storage). Browsing rewards/program info works fully inside the drawer.
 */
export default function MarselloPortalDrawer() {
  const [open, setOpen] = React.useState(false);
  const [everOpened, setEverOpened] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  React.useEffect(() => {
    if (!everOpened) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== PORTAL_ORIGIN) return;
      if (e.data === 'signin' || e.data === 'signup') {
        window.open(MARSELLO_PORTAL_URL, '_blank', 'noopener,noreferrer');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [everOpened]);

  return (
    <>
      {/* Compact login-style entry (was a tall orange pill): the same round
          button as the rest of the CTA stack with a person icon, and a small
          會員專區 label underneath. */}
      <button
        onClick={() => {
          setOpen(true);
          setEverOpened(true);
        }}
        className="group flex w-full flex-col items-center gap-1 outline-none"
        aria-label="會員專區"
      >
        <span className={ICON_BUTTON}>
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            className="h-4 w-4 md:h-6 md:w-6"
          >
            <path d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2.5c-3.6 0-8 1.8-8 5V21h16v-1.5c0-3.2-4.4-5-8-5z" />
          </svg>
        </span>
        <span className="text-center text-[10px] font-semibold leading-tight text-hk-orange md:text-xs">
          會員專區
        </span>
      </button>
      {/* The sidebar wrapper is fixed + transformed, which would trap
          position:fixed descendants — render the overlay from document.body. */}
      <ClientPortal>
        {open && (
          <button
            aria-label="關閉會員專區"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[99] bg-black/30"
          ></button>
        )}
        {/* Viewport-sized clip layer: the drawer is position:absolute inside
            it, so its closed (translated off the right edge) state is clipped
            and cannot extend the page's scroll width. Without this, iOS Safari
            counted the off-screen w-full drawer in the scrollable width, so the
            page could be dragged sideways to reveal a dark strip behind the
            contact CTA. pointer-events-none keeps the (invisible) layer from
            blocking the page. */}
        <div className="pointer-events-none fixed inset-0 z-[100] overflow-hidden">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="會員專區"
            aria-hidden={!open}
            className={clsx(
              // Transparent panel — the portal paints its own floating card.
              'absolute right-0 top-0 h-full w-full bg-transparent transition-transform duration-300 md:w-[470px]',
              open
                ? 'pointer-events-auto translate-x-0'
                : 'pointer-events-none translate-x-full',
            )}
          >
            {everOpened && (
              <iframe
                src={MARSELLO_PORTAL_URL}
                title="會員專區"
                className="h-full w-full border-0 bg-transparent"
                // The portal page background is transparent by design; keep
                // the iframe transparent too so the page shows through
                // around the portal's floating card instead of white.
                allowTransparency
                style={{background: 'transparent', colorScheme: 'normal'}}
                allow="clipboard-write"
              />
            )}
            <div className="absolute right-3 top-3 flex items-center gap-2">
              <a
                href={MARSELLO_PORTAL_URL}
                target="_blank"
                rel="noreferrer noopener"
                title="新視窗開啟"
                className={clsx(
                  'flex h-9 items-center rounded-full bg-white px-3 text-xs text-slate-600 shadow-md',
                  !open && 'hidden',
                )}
              >
                新視窗開啟 ↗
              </a>
              <button
                aria-label="關閉"
                onClick={() => setOpen(false)}
                className={clsx(
                  'flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md',
                  !open && 'hidden',
                )}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.5 9.35849L1.85849 15L0 13.1415L5.64151 7.5L0 1.85849L1.85849 0L7.5 5.64151L13.1415 0L15 1.85849L9.35849 7.5L15 13.1415L13.1415 15L7.5 9.35849Z"
                    fill="black"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </ClientPortal>
    </>
  );
}
